import {
  ROLES_ARVAL,
  ROLE_B2C,
  ROLE_BLIND_PUBLICATION,
  ROLE_LOGISTICS,
  ROLE_PARTNER_ADMIN,
  ROLE_PARTNER_READ_ONLY,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  useAuth,
} from 'auth'
import { useEffect, useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'

import NotFound from 'components/atoms/NotFound'
import PageLoading from 'components/atoms/PageLoading'
import CarsToBuyList from 'components/organisms/CarsToBuy'
import { getCarsToBuyUrl } from 'components/organisms/CarsToBuy/carsToBuyLib'
import useCarsToBuy from 'components/organisms/CarsToBuy/useCarsToBuy'
import useCarsToBuySession from 'components/organisms/CarsToBuy/useCarsToBuySession'
import useSelectedCarsToBuy from 'components/organisms/CarsToBuy/useSelectedCarsToBuy'

import useEntityData from 'hooks/useEntityData'
import useTimer from 'hooks/useTimer'

const isSessionOpen = (session) => {
  if (!session?.startsAt || !session?.endsAt) {
    return false
  }
  const start = new Date(session.startsAt)
  const end = new Date(session.endsAt)
  const now = new Date()
  return now > start && now < end
}

const sortCarsByTitle = (items) =>
  [...items].sort((a, b) => {
    const titleComparison = (a?.title || '').localeCompare(
      b?.title || '',
      'it',
      {
        sensitivity: 'base',
      }
    )

    if (titleComparison !== 0) {
      return titleComparison
    }

    return (a?.plate || '').localeCompare(b?.plate || '', 'it', {
      sensitivity: 'base',
    })
  })

export default function CarsToBuy() {
  const { getPartnerId } = useAuth()

  const { hasOneOfRoles } = useAuth()

  const admin = useMemo(() => hasOneOfRoles(ROLES_ARVAL), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () =>
      hasOneOfRoles([
        ROLE_READ_ONLY,
        ROLE_LOGISTICS,
        ROLE_B2C,
        ROLE_PARTNER_READ_ONLY,
        ROLE_POST_SALES,
      ]),
    [hasOneOfRoles]
  )
  const favoriteFunctionality = useMemo(
    () => !hasOneOfRoles(ROLES_ARVAL),
    [hasOneOfRoles]
  )

  const { partnerId } = useParams()

  const {
    isFetching: isFetchingSession,
    fetchSession,
    session,
    error,
  } = useCarsToBuySession('current')

  const {
    isFetching: isFetchingBlindSession,
    fetchSession: fetchBlindSession,
    session: blindSession,
    notFound: blindNotFound,
  } = useCarsToBuySession('current')

  const {
    isFetching: isFetchingPrevious,
    fetchSession: fetchPreviousSession,
    session: previousSession,
  } = useCarsToBuySession('previous')

  const {
    isFetching: isFetchingPreviousBlind,
    fetchSession: fetchPreviousBlindSession,
    session: previousBlindSession,
  } = useCarsToBuySession('previous')

  const {
    items: partners,
    isFetching: isFetchingPartners,
    refetch: fetchPartners,
  } = useEntityData({ limit: 9999 })

  const {
    isFetching: isFetchingCars,
    isUpdating: isUpdatingCars,
    fetchCarsToBuy,
    updateChoice,
    setReprice,
    addMessage,
    toggleFavorite,
    cars,
    error: errorCarsToBuy,
  } = useCarsToBuy()

  const {
    isFetching: isFetchingBlindCars,
    isUpdating: isUpdatingBlindCars,
    fetchCarsToBuy: fetchBlindCarsToBuy,
    setBlindPrice,
    cars: blindCars,
  } = useCarsToBuy()

  const { countDown, initializeTimer } = useTimer(
    session?.startsAt,
    session?.endsAt
  )

  const { countDown: blindCountDown, initializeTimer: initializeBlindTimer } =
    useTimer(blindSession?.startsAt, blindSession?.endsAt)

  const { fetchSelectedCarsToBuy, cars: selectedCarsToBuy } =
    useSelectedCarsToBuy()

  const blindCarsSessionId = useMemo(() => {
    if (isSessionOpen(session)) {
      return session?.sessionId
    }

    return blindSession?.sessionId || session?.sessionId
  }, [blindSession?.sessionId, session])

  const canAccessBlind = useMemo(
    () =>
      hasOneOfRoles([
        ...ROLES_ARVAL,
        ROLE_BLIND_PUBLICATION,
        ROLE_PARTNER_ADMIN,
      ]),
    [hasOneOfRoles]
  )

  useEffect(() => {
    if (!admin) {
      return
    }

    fetchPartners(`partners/`)
  }, [admin, fetchPartners])

  const myPartnerId = useMemo(
    () =>
      admin
        ? partnerId &&
          (partners.length === 0 ||
            partners.find((partner) => partner.partnerId === partnerId))
          ? partnerId
          : partners?.[0]?.partnerId
        : getPartnerId(),
    [admin, getPartnerId, partnerId, partners]
  )

  const shouldFetchBlind = useMemo(() => {
    if (!canAccessBlind) {
      return false
    }
    if (admin) {
      return true
    }
    return true
  }, [admin, canAccessBlind])

  const showBlindControls = useMemo(() => {
    if (!canAccessBlind) {
      return false
    }
    if (admin) {
      return true
    }
    return !blindNotFound
  }, [admin, blindNotFound, canAccessBlind])

  useEffect(() => {
    if (
      !myPartnerId ||
      (session?.sessionId && session?.partnerId === myPartnerId)
    ) {
      return
    }

    fetchSession(myPartnerId)
  }, [
    fetchSession,
    myPartnerId,
    session?.sessionId,
    countDown.expired,
    session?.partnerId,
  ])

  useEffect(() => {
    if (
      !shouldFetchBlind ||
      !myPartnerId ||
      (blindNotFound && blindSession?.partnerId === myPartnerId) ||
      (blindSession?.sessionId && blindSession?.partnerId === myPartnerId)
    ) {
      return
    }

    fetchBlindSession(myPartnerId, false, true, { ignoreNotFound: true })
  }, [
    fetchBlindSession,
    myPartnerId,
    blindSession?.sessionId,
    blindSession?.partnerId,
    blindNotFound,
    shouldFetchBlind,
  ])

  useEffect(() => {
    if (session?.sessionId) {
      initializeTimer()
    }
  }, [initializeTimer, session?.sessionId])

  useEffect(() => {
    if (blindSession?.sessionId) {
      initializeBlindTimer()
    }
  }, [initializeBlindTimer, blindSession?.sessionId])

  useEffect(() => {
    if (!myPartnerId) {
      return
    }

    fetchPreviousSession(myPartnerId, true)
  }, [fetchPreviousSession, myPartnerId])

  useEffect(() => {
    if (
      !myPartnerId ||
      !shouldFetchBlind ||
      (blindNotFound && blindSession?.partnerId === myPartnerId)
    ) {
      return
    }

    fetchPreviousBlindSession(myPartnerId, true, true, {
      ignoreNotFound: true,
    })
  }, [
    fetchPreviousBlindSession,
    myPartnerId,
    shouldFetchBlind,
    blindNotFound,
    blindSession?.partnerId,
  ])

  useEffect(() => {
    if (!session?.sessionId) {
      return
    }
    if (!session?.showOtherPartners || countDown.expired) {
      return
    }
    fetchSelectedCarsToBuy(session.sessionId, false)

    const timer = setInterval(
      () => fetchSelectedCarsToBuy(session.sessionId, false),
      5000
    )
    return () => {
      clearInterval(timer)
    }
  }, [
    countDown.expired,
    fetchSelectedCarsToBuy,
    session.sessionId,
    session.showOtherPartners,
  ])

  useEffect(() => {
    if (!session?.sessionId || !myPartnerId) {
      return
    }

    fetchCarsToBuy(session?.sessionId, myPartnerId)
  }, [fetchCarsToBuy, myPartnerId, session?.sessionId])

  useEffect(() => {
    if (!blindCarsSessionId || !myPartnerId || !showBlindControls) {
      return
    }

    fetchBlindCarsToBuy(blindCarsSessionId, myPartnerId, true)
  }, [blindCarsSessionId, fetchBlindCarsToBuy, myPartnerId, showBlindControls])

  const readOnly = useMemo(
    () => isFetchingCars || isFetchingSession || isUpdatingCars,
    [isFetchingCars, isFetchingSession, isUpdatingCars]
  )

  const blindReadOnly = useMemo(
    () => isFetchingBlindCars || isFetchingBlindSession || isUpdatingBlindCars,
    [isFetchingBlindCars, isFetchingBlindSession, isUpdatingBlindCars]
  )

  const blindOffersRemaining = useMemo(() => {
    if (!showBlindControls) {
      return null
    }

    const blindCap = Number(blindSession?.partnerBlindCaps?.firstChoice ?? 0)
    const usedOffers = blindCars.filter(
      (car) => car.blindPrice !== null && car.blindPrice !== undefined
    ).length
    const remaining = blindCap - usedOffers

    return Number.isFinite(remaining) ? Math.max(remaining, 0) : null
  }, [blindCars, blindSession?.partnerBlindCaps, showBlindControls])

  const mergedCars = useMemo(() => {
    if (!showBlindControls || blindCars.length === 0) {
      return sortCarsByTitle(cars)
    }

    const blindByPlate = new Map(
      blindCars.map((car) => [
        car.plate,
        {
          blindPrice: car.blindPrice ?? car.reprice,
          blindOfferCount: car.blindOfferCount,
        },
      ])
    )

    const carPlates = new Set(cars.map((car) => car.plate))

    const merged = cars.map((car) => {
      const blindData = blindByPlate.get(car.plate)
      return {
        ...car,
        ...(blindData || {}),
      }
    })

    const blindOnly = blindCars
      .filter((car) => !carPlates.has(car.plate))
      .map((car) => {
        const blindData = blindByPlate.get(car.plate)
        return blindData
          ? {
              ...car,
              ...blindData,
              firstChoice: null,
              secondChoice: null,
            }
          : {
              ...car,
              firstChoice: null,
              secondChoice: null,
            }
      })

    const allCars = [...merged, ...blindOnly]
    return sortCarsByTitle(allCars)
  }, [blindCars, cars, showBlindControls])

  const nonBlindIsOpen = useMemo(() => isSessionOpen(session), [session])
  const blindAvailable =
    showBlindControls && !!blindSession?.sessionId && !blindNotFound
  const useBlindAsPrimary = useMemo(
    () => !nonBlindIsOpen && blindAvailable,
    [blindAvailable, nonBlindIsOpen]
  )

  const primarySession = useMemo(
    () => (useBlindAsPrimary ? blindSession : session),
    [blindSession, session, useBlindAsPrimary]
  )
  const primaryPreviousSession = useMemo(
    () => (useBlindAsPrimary ? previousBlindSession : previousSession),
    [previousBlindSession, previousSession, useBlindAsPrimary]
  )
  const primaryCountDown = useMemo(
    () => (useBlindAsPrimary ? blindCountDown : countDown),
    [blindCountDown, countDown, useBlindAsPrimary]
  )
  const primaryCars = useMemo(
    () => (useBlindAsPrimary ? sortCarsByTitle(blindCars) : mergedCars),
    [blindCars, mergedCars, useBlindAsPrimary]
  )
  const primaryReadOnly = useMemo(
    () => (useBlindAsPrimary ? blindReadOnly : readOnly),
    [blindReadOnly, readOnly, useBlindAsPrimary]
  )
  const primarySelectedCarsToBuy = useMemo(
    () => (useBlindAsPrimary ? [] : selectedCarsToBuy),
    [selectedCarsToBuy, useBlindAsPrimary]
  )
  const primaryIsBlindPublication = useBlindAsPrimary
  const primaryShowBlindPrice = !useBlindAsPrimary && showBlindControls

  const isLoadingPrimary = useBlindAsPrimary
    ? (isFetchingBlindCars && !blindCars.length) ||
      isFetchingBlindSession ||
      isFetchingPreviousBlind
    : (isFetchingCars && !cars.length) ||
      isFetchingSession ||
      isFetchingPrevious

  const isLoading =
    isLoadingPrimary ||
    isFetchingPartners ||
    (!useBlindAsPrimary &&
      shouldFetchBlind &&
      ((isFetchingBlindCars && !blindCars.length) ||
        isFetchingBlindSession ||
        isFetchingPreviousBlind))

  const nonBlindNotFound =
    (error && Number(error?.response?.status) === 404) ||
    (errorCarsToBuy && Number(errorCarsToBuy?.response?.status) === 404)

  const notFound = nonBlindNotFound && !blindAvailable

  return (
    <div className="content-container">
      {isLoading ? (
        <PageLoading />
      ) : notFound ? (
        <NotFound />
      ) : myPartnerId !== partnerId ? (
        <Navigate to={getCarsToBuyUrl(myPartnerId)} />
      ) : (
        <CarsToBuyList
          session={primarySession}
          previousSession={primaryPreviousSession}
          admin={admin}
          partnerId={myPartnerId}
          partners={partners}
          updateChoice={updateChoice}
          setReprice={setReprice}
          addMessage={addMessage}
          toggleFavorite={toggleFavorite}
          cars={primaryCars}
          selectedCarsToBuy={primarySelectedCarsToBuy}
          readOnly={primaryReadOnly}
          contentIsReadOnly={userRoleHasReadOnly}
          countDown={primaryCountDown}
          showFavorites={favoriteFunctionality}
          showBlindPrice={primaryShowBlindPrice}
          blindOffersRemaining={blindOffersRemaining}
          blindReadOnly={blindReadOnly}
          setBlindPrice={setBlindPrice}
          blindSessionId={blindSession?.sessionId}
          blindCaps={blindSession?.partnerBlindCaps}
          isBlindPublication={primaryIsBlindPublication}
        />
      )}
    </div>
  )
}
